import { useContext, useEffect, useState, useMemo } from "react";
import { withApollo } from "react-apollo";
import BookingFilter from "./BookingFilter";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import BookingList from "./BookingList";
import DeleteBooking from "../../DeleteBooking";
import RejectBooking from "../../RejectBooking";
import MyBookingMultiDeleteDialog from "../../booking-instruments/my-bookings/my-booking-list-view/my-booking-multi-delete-button/MyBookingMultiDeleteDialog";
import HazardousRejectDialog from "../../hazardous-materials/HazardousRejectDialog";
import AssistanceAssignmentDialog from "../../hazardous-materials/AssistanceAssignmentDialog";
import BookingNotificationStatus from "../../booking-calendars/booking-notification-status/BookingNotificationStatus";
import { timeoutCallbackFactory } from "../../booking-calendars/booking-notification-status/timeoutCallbackFactory";
import { useBookingOperations, useBookingSubscriptionStatus, BOOKING_OPERATIONS } from "../../booking-operations";
import { quickEditTracker } from "../../booking-operations/quickEditTracker";
import { PaginationContext } from "../../../components/shared/pagination/PaginationContext";
import { getWeekStartAndEndInISOString } from "../../../utils/helpers/text";
import { useSelector } from "react-redux";

function DropBooking({
  setSubscriptionStatus,
  subscriptionStatus,
  setPopupEnableStatus,
  eventID = null,
  urlAction = null,
  client
}) {
  const { trackPageView } = useMatomo();
  const user = useSelector((store) => store.user);
  const [resetAll, setResetAll] = useState(false);
  const [showAdvanceFilter, setShowAdvanceFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Initialize bookingIdFilter with eventID if it exists
  const [bookingIdFilter, setBookingIdFilter] = useState(eventID);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [disableResetAll, setDisableResetAll] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState([
    getWeekStartAndEndInISOString("start", user.timezone),
    getWeekStartAndEndInISOString("end", user.timezone),
  ]);
  const [tempRowData, setTempRowData] = useState([]);
  const [isDropBooking, setIsDropBooking] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [openMultiDeleteDialog, setOpenMultiDeleteDialog] = useState(false);
  const [rowEquipmentId, setRowEquipmentId] = useState(null);

  // Reject booking states
  const [isRejectBooking, setIsRejectBooking] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectRowEquipmentId, setRejectRowEquipmentId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [openRejectBookingDialog, setOpenRejectBookingDialog] = useState(false);

  // Hazardous rejection states
  const [openHazardousRejectDialog, setOpenHazardousRejectDialog] = useState(false);
  const [hazardousBookingToReject, setHazardousBookingToReject] = useState(null);

  // Assistance assignment states
  const [openAssistanceDialog, setOpenAssistanceDialog] = useState(false);
  const [bookingForAssistance, setBookingForAssistance] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [_gridApi, setGridApi] = useState(null);

  const { dispatchAction } = useContext(PaginationContext);
  const [status, setStatus] = useState(false);
  useEffect(() => {
    setTempRowData([]);
    trackPageView({
      documentTitle: "Admin Panel/Drop booking",
      href: `${window.location?.origin}/AdminPanel/DropBooking`,
    });
    // eslint-disable-next-line
  }, []);

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      // Clear booking filter when leaving the page
      if (eventID && urlAction) {
        setBookingIdFilter(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize booking operations
  const { createBookingCallback } = useBookingOperations();

  const bookingCallback = useMemo(() => createBookingCallback({
    operation: BOOKING_OPERATIONS.CANCEL,
    clearPagination: false,
    resetStates: {
      multiDeleteDialog: setOpenMultiDeleteDialog,
      rejectBookingDialog: setOpenRejectBookingDialog,
      status: setStatus,
      dropBooking: setIsDropBooking,
      dialog: setIsDialogOpen,
      hazardousRejectDialog: setOpenHazardousRejectDialog,
      assistanceDialog: setOpenAssistanceDialog,
      editMode: setIsEditMode,
    },
    clearSelections: [setTempRowData],
    onSuccess: (data) => {
      const commandId = data?.data?.onUpdateBookingCommand?.id;

      if (quickEditTracker.isQuickEdit(commandId)) {
        quickEditTracker.remove(commandId);
        return;
      }

      setHazardousBookingToReject(null);
      setBookingForAssistance(null);
      // Manually clear the fetching state since dispatchAction might not be available in the callback
      if (dispatchAction) {
        dispatchAction({ type: "fetching", payload: false });
        dispatchAction({ type: "clearAll" });
      }
    },
    skipCheckInSucceeded: true, // DropBooking doesn't need CHECK_IN_SUCCEEDED handling
  }), [createBookingCallback, dispatchAction]);

  // Handle subscription status notifications
  useBookingSubscriptionStatus(subscriptionStatus, setSubscriptionStatus);

  // Handle URL actions - filter the table by bookingId
  useEffect(() => {
    if (eventID && urlAction) {
      // Set the booking filter when eventID is present
      setBookingIdFilter(eventID);
    } else if (!eventID && bookingIdFilter !== null) {
      // Clear the filter when there's no eventID
      setBookingIdFilter(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventID, urlAction]);

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <BookingFilter
          resetAll={resetAll}
          setResetAll={setResetAll}
          setSelectedEquipment={setSelectedEquipment}
          showAdvanceFilter={showAdvanceFilter}
          setShowAdvanceFilter={setShowAdvanceFilter}
          selectedDateRange={selectedDateRange}
          setSelectedDateRange={setSelectedDateRange}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          bookingIdFilter={bookingIdFilter}
          setBookingIdFilter={setBookingIdFilter}
          tempRowData={tempRowData}
          setTempRowData={setTempRowData}
          disableResetAll={disableResetAll}
          setDisableResetAll={setDisableResetAll}
        />
        <BookingList
          showAdvanceFilter={showAdvanceFilter}
          selectedDateRange={selectedDateRange}
          setSelectedEquipment={setSelectedEquipment}
          selectedEquipment={selectedEquipment}
          setSelectedDateRange={setSelectedDateRange}
          resetAll={resetAll}
          setIsDropBooking={setIsDropBooking}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setIsDialogOpen={setIsDialogOpen}
          setOpenMultiDeleteDialog={setOpenMultiDeleteDialog}
          setOpenRejectBookingDialog={setOpenRejectBookingDialog}
          setRowEquipmentId={setRowEquipmentId}
          tempRowData={tempRowData}
          setTempRowData={setTempRowData}
          setDisableResetAll={setDisableResetAll}
          setResetAll={setResetAll}
          setPopupEnableStatus={setPopupEnableStatus}
          setIsRejectBooking={setIsRejectBooking}
          setIsRejectDialogOpen={setIsRejectDialogOpen}
          setRejectRowEquipmentId={setRejectRowEquipmentId}
          // Hazardous rejection props
          setOpenHazardousRejectDialog={setOpenHazardousRejectDialog}
          setHazardousBookingToReject={setHazardousBookingToReject}
          // Assistance assignment props
          setOpenAssistanceDialog={setOpenAssistanceDialog}
          setBookingForAssistance={setBookingForAssistance}
          setIsEditMode={setIsEditMode}
          status={status}
          setStatus={setStatus}
          // URL action context
          urlAction={urlAction}
          bookingIdFilter={bookingIdFilter}
          onGridApiReady={setGridApi}
        />
      </div>
      <BookingNotificationStatus
        tryTimes={3}
        timeout={5 * 60 * 1000}
        onTimeoutCallback={timeoutCallbackFactory()}
        onBookingCallback={bookingCallback}
      />



      {isDropBooking && (
        <DeleteBooking
          setIsDropBooking={setIsDropBooking}
          setIsDialogOpen={setIsDialogOpen}
          isDialogOpen={isDialogOpen}
          force={true}
          item={rowEquipmentId}
          status={status}
          setStatus={setStatus}
        />
      )}

      {isRejectBooking && (
        <RejectBooking
          setIsRejectBooking={setIsRejectBooking}
          setIsRejectDialogOpen={setIsRejectDialogOpen}
          isRejectDialogOpen={isRejectDialogOpen}
          force={true}
          item={rejectRowEquipmentId}
          status={status}
          setStatus={setStatus}
        />
      )}

      {
        openMultiDeleteDialog && (
          <MyBookingMultiDeleteDialog
            openMultiDeleteDialog={openMultiDeleteDialog}
            setOpenMultiDeleteDialog={setOpenMultiDeleteDialog}
            force={true}
            selectedEquipment={selectedEquipment}
            status={status}
            setStatus={setStatus}
            setSelectedEquipment={setSelectedEquipment}
          />
        )
      }

      {/* Hazardous Rejection Dialog */}
      {openHazardousRejectDialog && (
        <HazardousRejectDialog
          openRejectDialog={openHazardousRejectDialog}
          setOpenRejectDialog={setOpenHazardousRejectDialog}
          booking={hazardousBookingToReject}
          user={user}
          status={status}
          setStatus={setStatus}
        />
      )}

      {/* Assistance Assignment Dialog */}
      {openAssistanceDialog && (
        <AssistanceAssignmentDialog
          open={openAssistanceDialog}
          onClose={() => {
            setOpenAssistanceDialog(false);
            setBookingForAssistance(null);
            setIsEditMode(false);
          }}
          booking={bookingForAssistance}
          client={client}
          onApprovalComplete={() => {
            setOpenAssistanceDialog(false);
            setBookingForAssistance(null);
            setIsEditMode(false);
            // Allow UI to refresh after approval
            setTimeout(() => {
              dispatchAction({
                type: "refresh",
              });
            }, 500);
          }}
          user={user}
          status={status}
          setStatus={setStatus}
          isEditMode={isEditMode}
        />
      )}
    </>
  );
}

export default withApollo(DropBooking);
